import { z } from 'zod';
import { NAV_APPS, REMINDER_OPTIONS, ROLES, USER_COLORS } from '../constants';

/**
 * Request schemas shared by the API (authoritative validation) and the web app
 * (instant feedback in forms). Messages are shown to users as they are.
 */

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Hasło musi mieć co najmniej ${PASSWORD_MIN_LENGTH} znaków.`)
  .max(PASSWORD_MAX_LENGTH, `Hasło może mieć najwyżej ${PASSWORD_MAX_LENGTH} znaków.`);

export const loginNameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'Login musi mieć co najmniej 3 znaki.')
  .max(32, 'Login może mieć najwyżej 32 znaki.')
  .regex(/^[a-z0-9._-]+$/, 'Login może zawierać małe litery bez polskich znaków, cyfry, kropkę, myślnik i podkreślenie.');

export const personNameSchema = z
  .string()
  .trim()
  .min(2, 'Wpisz imię i nazwisko.')
  .max(120, 'Imię i nazwisko może mieć najwyżej 120 znaków.');

export const loginRequestSchema = z.object({
  login: z.string().trim().toLowerCase().min(1, 'Wpisz login.').max(64, 'Nieprawidłowy login.'),
  password: z.string().min(1, 'Wpisz hasło.').max(PASSWORD_MAX_LENGTH, 'Nieprawidłowe hasło.'),
});
export type LoginRequest = z.input<typeof loginRequestSchema>;

/** `currentPassword` may be omitted only when the user must replace a temporary password. */
export const changePasswordSchema = z
  .object({
    currentPassword: z.string().max(PASSWORD_MAX_LENGTH).optional(),
    newPassword: passwordSchema,
  })
  .refine((d) => d.newPassword !== d.currentPassword, {
    path: ['newPassword'],
    message: 'Nowe hasło musi różnić się od obecnego.',
  });
export type ChangePasswordRequest = z.input<typeof changePasswordSchema>;

export const userCreateSchema = z
  .object({
    name: personNameSchema,
    login: loginNameSchema,
    role: z.enum(ROLES, 'Wybierz rolę.'),
    color: z.enum(USER_COLORS, 'Wybierz kolor.'),
    password: passwordSchema,
  })
  .refine((d) => d.password.toLowerCase() !== d.login, {
    path: ['password'],
    message: 'Hasło nie może być takie samo jak login.',
  });
export type UserCreateRequest = z.input<typeof userCreateSchema>;

export const userUpdateSchema = z
  .object({
    name: personNameSchema,
    login: loginNameSchema,
    role: z.enum(ROLES, 'Wybierz rolę.'),
    color: z.enum(USER_COLORS, 'Wybierz kolor.'),
  })
  .partial();
export type UserUpdateRequest = z.input<typeof userUpdateSchema>;

export const resetPasswordSchema = z.object({ password: passwordSchema });
export type ResetPasswordRequest = z.input<typeof resetPasswordSchema>;

export const morningTimeSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Wybierz godzinę w formacie GG:MM.');

export const reminderMinutesSchema = z
  .number()
  .int()
  .refine((v) => (REMINDER_OPTIONS as readonly number[]).includes(v), 'Wybierz jedną z dostępnych opcji.')
  .nullable();

export const settingsUpdateSchema = z
  .object({
    navApp: z.enum(NAV_APPS, 'Wybierz aplikację do nawigacji.'),
    morningSummaryEnabled: z.boolean(),
    morningSummaryTime: morningTimeSchema,
    reminderMinutes: reminderMinutesSchema,
    notifyWorkerActivity: z.boolean(),
  })
  .partial();
export type SettingsUpdateRequest = z.input<typeof settingsUpdateSchema>;
